/**
 * Widget A/B tests — the ONE typed contract for testing the widget's VISUALS
 * against each other: the lower bar (its style, or questions only against the
 * full bar) and the inline containers (their style).
 *
 * How a test runs:
 *
 *   - The definition rides the preload (`config.experiments`), which is the
 *     same for every visitor and publicly cached. It carries only tests that
 *     are running NOW; widget-api rebuilds it every 60s, so a scheduled start
 *     or end takes effect within a minute.
 *   - The ASSIGNMENT is made in the visitor's browser (the snippet), because
 *     the preload cannot carry anything per-visitor. The first time a visitor
 *     meets a test they are drawn into a variant by its weights, and the draw
 *     is stored in first-party localStorage (`AB_STORAGE_KEY`). Every later
 *     page reads the stored draw, so a visitor keeps their variant for as long
 *     as the test runs — even if the weights were somehow changed. A browser
 *     that refuses storage falls back to a hash of a stable seed (the session
 *     id), which is also stable for that session.
 *   - The widget reports an `experiment_exposure` event when the tested
 *     surface is actually in play on a page. Results join each session's
 *     FIRST exposure to its session rollup (meaningful, conversation, CTA click,
 *     converted), so a test reads the same outcomes Conversion Intelligence
 *     does, test/bot sessions excluded the same way.
 *
 * The first variant is the CONTROL: lift and confidence are measured against it.
 *
 * Bundle skew: an older bh.js ignores `config.experiments`, shows the site's
 * normal settings and reports no exposure — its visitors are simply not in the
 * test. An older widget-api rejects the exposure event; nothing else breaks.
 *
 * Pure module (no zod) so the snippet (bh.js) can import it — the bundle gate
 * in widget/build.ts bans zod there. Zod schemas live in ./experiments-schema.ts.
 */

/** What a test varies. One running test per surface at a time. */
export const EXPERIMENT_SURFACES = ["lower_bar", "inline"] as const;
export type ExperimentSurface = (typeof EXPERIMENT_SURFACES)[number];

export function isExperimentSurface(v: unknown): v is ExperimentSurface {
  return typeof v === "string" && (EXPERIMENT_SURFACES as readonly string[]).includes(v);
}

/** Stored lifecycle. `running` covers "scheduled" and "finished" too — see
 *  `experimentPhase`, which reads the dates. */
export const EXPERIMENT_STATUSES = ["draft", "running", "stopped"] as const;
export type ExperimentStatus = (typeof EXPERIMENT_STATUSES)[number];

/** What the dashboard shows: the stored status read against the clock. */
export type ExperimentPhase = "draft" | "scheduled" | "running" | "finished" | "stopped";

/** Variant keys, in order. The first is the control. */
export const VARIANT_KEYS = ["A", "B", "C", "D"] as const;
export type VariantKey = (typeof VARIANT_KEYS)[number];
export const MIN_VARIANTS = 2;
export const MAX_VARIANTS = VARIANT_KEYS.length;

/**
 * One variant as it travels to the browser. Every setting is optional: an
 * absent one means "the site's normal setting", so a variant can change one
 * thing and leave the rest alone. Settings are free-form strings on the wire on
 * purpose (as the rest of the config message): the snippet narrows each with
 * its own `is*` guard and ignores what it does not know, so a value added later
 * degrades on an old bundle instead of failing the whole preload.
 *
 *   lower_bar: `skin` (a living-bar skin), `barContent` ("full" | "nudges")
 *   inline:    `inlineMode` ("expand" | "open" | "agent" | "nudges")
 */
export type DeliveredVariant = {
  key: string;
  /** Share of visitors, any positive number; normalised against the sum. */
  weight: number;
  skin?: string;
  barContent?: string;
  inlineMode?: string;
};

export type DeliveredExperiment = {
  id: string;
  surface: string;
  /** Epoch ms the test ends, or null for a test that runs until stopped. Lets a
   *  stale cached preload stop applying a test that has already ended. */
  endsAt: number | null;
  variants: DeliveredVariant[];
};

/** The event the widget reports when a tested surface is in play. */
export const EXPERIMENT_EXPOSURE_EVENT = "experiment_exposure";

/** First-party localStorage key holding `{ seed, assigned: { [testId]: key } }`.
 *  It survives `?bh_new_session=1` (that clears the conversation, not who the
 *  visitor is), so a new session never moves a visitor to another version.
 *  The Test the widget page's Start fresh clears every bh_ and bhai key, so a
 *  tester there gets a new draw. */
export const AB_STORAGE_KEY = "bhai_ab";

/** `?bh_ab=B` forces variant B in every running test on this page, for
 *  looking at a variant. A forced view reports no exposure, so it never lands
 *  in the results. */
export const AB_FORCE_PARAM = "bh_ab";

/**
 * 32-bit FNV-1a. Deterministic across engines, which is the whole point: the
 * hash fallback must give the same visitor the same variant on every page.
 */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Pick a variant for a draw `u` in [0, 1). Weights are normalised against
 * their sum; non-positive or non-finite weights take no share. Returns null
 * when no variant can be picked (no variants, or no positive weight).
 */
export function variantForDraw(variants: readonly DeliveredVariant[], u: number): DeliveredVariant | null {
  const usable = variants.filter((v) => Number.isFinite(v.weight) && v.weight > 0);
  const total = usable.reduce((s, v) => s + v.weight, 0);
  if (usable.length === 0 || total <= 0) return null;
  const target = Math.min(Math.max(u, 0), 0.999999999) * total;
  let acc = 0;
  for (const v of usable) {
    acc += v.weight;
    if (target < acc) return v;
  }
  return usable[usable.length - 1];
}

/** A deterministic draw in [0, 1) for one seed in one test. */
export function hashDraw(seed: string, experimentId: string): number {
  return hashString(`${experimentId}:${seed}`) / 0x100000000;
}

/** Tests from a (possibly stale, cached) preload that still apply at `now`. */
export function liveExperiments(
  delivered: readonly DeliveredExperiment[],
  now: number,
): DeliveredExperiment[] {
  return delivered.filter((e) => e.endsAt == null || e.endsAt > now);
}

/** The stored assignment document. Read defensively: it lives in a host page's
 *  storage, which anything on that page can write. */
export type AbAssignments = { seed: string; assigned: { [experimentId: string]: string } };

export function parseAbAssignments(raw: string | null): AbAssignments | null {
  if (!raw) return null;
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof v !== "object" || v === null) return null;
  const seed = "seed" in v ? v.seed : null;
  const assigned = "assigned" in v ? v.assigned : null;
  if (typeof seed !== "string" || seed.length === 0) return null;
  const out: AbAssignments = { seed, assigned: {} };
  if (typeof assigned === "object" && assigned !== null) {
    for (const [k, val] of Object.entries(assigned)) {
      if (typeof val === "string") out.assigned[k] = val;
    }
  }
  return out;
}

/**
 * The variant this visitor sees in one test, and whether the stored document
 * changed (so the caller writes it back).
 *
 *   - `forced` (from `?bh_ab=`) wins when that key exists in the test.
 *   - A stored draw that still names a variant of the test is kept — that is
 *     the stability guarantee.
 *   - Otherwise draw from the seed (a hash, so a browser that cannot store the
 *     draw still gets the same answer from the same seed) and store it.
 */
export function assignVariant(
  experiment: DeliveredExperiment,
  doc: AbAssignments,
  forced: string | null,
): { variant: DeliveredVariant | null; forced: boolean; changed: boolean } {
  if (forced) {
    const f = experiment.variants.find((v) => v.key === forced);
    if (f) return { variant: f, forced: true, changed: false };
  }
  const storedKey = doc.assigned[experiment.id];
  const stored = storedKey ? experiment.variants.find((v) => v.key === storedKey) : undefined;
  if (stored) return { variant: stored, forced: false, changed: false };
  const picked = variantForDraw(experiment.variants, hashDraw(doc.seed, experiment.id));
  if (!picked) return { variant: null, forced: false, changed: false };
  doc.assigned[experiment.id] = picked.key;
  return { variant: picked, forced: false, changed: true };
}

/** The stored status read against the clock. */
export function experimentPhase(
  e: { status: ExperimentStatus; startsAt: number | null; endsAt: number | null },
  now: number,
): ExperimentPhase {
  if (e.status === "draft") return "draft";
  if (e.status === "stopped") return "stopped";
  if (e.startsAt != null && e.startsAt > now) return "scheduled";
  if (e.endsAt != null && e.endsAt <= now) return "finished";
  return "running";
}
